import React, {useContext, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import {Tabs, Tab, Box, Typography, Avatar, Button} from "@mui/material";
import {Dashboard} from "@mui/icons-material";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import SeatMap from "../components/SeatMap";
import {AuthContext} from "../context/AuthContext";
import LocalLibraryOutlinedIcon from "@mui/icons-material/LocalLibraryOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import HostelBookingHistory from "./HostelBookingHistory";
import LibraryBookingHistory from "./LibraryBookingHistory";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import HostelBookingForm from "./HostelBookingForm";
import LibraryBookingForm from "./LibraryBookingForm";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import StudentComplaints from "./StudentComplaints";
import {StarRate} from "@mui/icons-material";
import StudentReview from "./StudentReview";
import StudentProfile from "./StudentProfile";
import RefreshIcon from "@mui/icons-material/Refresh";
import StudentBill from "./StudentBill";
import StudentBedSeatNotes from "../components/StudentBedSeatNotes";

const API = process.env.REACT_APP_API_URL;

const menuItems = [{text: "Dashboard", icon: <Dashboard/>}, {
    text: "Hostel History", icon: <ApartmentOutlinedIcon/>
}, {text: "Library History", icon: <LocalLibraryOutlinedIcon/>}, {
    text: "Complaints", icon: <ReportProblemIcon/>
}, {text: "Bill", icon: <ReceiptOutlinedIcon/>}, {text: "Review", icon: <StarRate/>}, {
    text: "Logout", icon: <LogoutOutlinedIcon/>
},];

const StudentDashboard = () => {
    const {logout} = useContext(AuthContext);
    const navigate = useNavigate();

    const [hostelSeats, setHostelSeats] = useState([]);
    const [librarySeats, setLibrarySeats] = useState([]);
    const [profile, setProfile] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [selectedTab, setSelectedTab] = useState(() => {
        return localStorage.getItem("student_selected_menu") || "Dashboard";
    });

    const [activeTab, setActiveTab] = useState(() => {
        return Number(localStorage.getItem("student_dashboard_tab") || 0);
    });
    const [mobileOpen, setMobileOpen] = useState(false);
    const [selectedBedId, setSelectedBedId] = useState(null);
    const [selectedSeatId, setSelectedSeatId] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const sideNavigationHandler = (route) => {
        if (route === "Logout") {
            handleLogout();
            return;
        }

        if (route === "Dashboard") {
            fetchAllData();
        }

        setSelectedTab(route);
        localStorage.setItem("student_selected_menu", route);
        setMobileOpen(false);
    };

    const fetchAllData = async () => {
        const loadingToast = toast.loading("Loading...");
        try {
            const token = localStorage.getItem("token");
            const config = {headers: {Authorization: `Bearer ${token}`}};

            const [hostelRes, libraryRes, profileRes] = await Promise.all([axios.get(`${API}hostel/beds/live-map/`, config), axios.get(`${API}library/seats/live-map/`, config), axios.get(`${API}users/me/`, config),]);

            setHostelSeats(hostelRes.data);
            setLibrarySeats(libraryRes.data);
            setProfile(profileRes.data);
            localStorage.setItem("student_id", profileRes.data.id);
            toast.dismiss(loadingToast);
        } catch (err) {
            toast.dismiss(loadingToast);
            toast.error("Session expired. Please login again");
            logout();
            navigate("/");
        }
    };

    const refreshAvailabilityMaps = async () => {
        setIsRefreshing(true);
        const loadingToast = toast.loading("🔄 Refreshing availability maps...");
        try {
            const token = localStorage.getItem("token");
            const config = {headers: {Authorization: `Bearer ${token}`}};

            // Fetch fresh data from both APIs
            const [hostelRes, libraryRes] = await Promise.all([axios.get(`${API}hostel/beds/live-map/`, config), axios.get(`${API}library/seats/live-map/`, config),]);

            // Update the state with fresh data
            setHostelSeats(hostelRes.data);
            setLibrarySeats(libraryRes.data);

            toast.dismiss(loadingToast);
            toast.success("✅ Availability maps refreshed successfully!", {
                duration: 2000, icon: "🔄",
            });
        } catch (err) {
            toast.dismiss(loadingToast);
            toast.error("❌ Failed to refresh availability maps");
            console.error("Refresh error:", err);
        } finally {
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        fetchAllData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchAllData();
        // Restore scroll position after refresh
        window.scrollTo(0, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    // Close mobile drawer when selectedTab changes
    useEffect(() => {
        if (mobileOpen) {
            setMobileOpen(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTab]);

    useEffect(() => {
        // Scroll to top when component mounts or refreshes
        window.scrollTo(0, 0);
    }, []);

    const handleLogout = () => {
        logout();
        navigate("/");
    };

    const handleHostelSeatClick = (seat) => {
        setSelectedBedId(seat?.id);
    };

    const handleLibrarySeatClick = (seat) => {
        setSelectedSeatId(seat?.id);
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        localStorage.setItem("student_dashboard_tab", newValue);
    };

    const toggleDropdown = () => setDropdownOpen((prev) => !prev);

    const drawerContent = (<Box
        className="h-full bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 text-white relative overflow-hidden">
        {/* Background decoration */}
        <Box className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-500"/>
        <Box
            className="absolute top-20 right-0 w-32 h-32 bg-blue-500/10 rounded-full -translate-y-16 translate-x-16"/>
        <Box
            className="absolute bottom-20 left-0 w-24 h-24 bg-purple-500/10 rounded-full translate-y-12 -translate-x-12"/>

        <Box className="relative z-10 h-full flex flex-col">
            <Box className="p-6 border-b border-gray-700/50">
                <Box className="flex items-center gap-4">
                    <Avatar
                        src="/images/logo.png"
                        alt="A-1 Ajinkya Hostel"
                        className="w-14 h-14 border-2 border-blue-500/30 shadow-lg"
                    />
                    <Box>
                        <Typography variant="h6" className="font-bold text-white mb-1">
                            Ajinkya Hostel
                        </Typography>
                        <Typography
                            variant="caption"
                            className="text-gray-400 bg-gray-800/50 px-2 py-1 rounded-full"
                        >
                            Student Portal
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <List className="py-4 flex-1">
                {menuItems.map((item, index) => (<ListItem
                    button
                    key={item.text}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        sideNavigationHandler(item.text);
                        // Force close drawer with multiple attempts
                        setMobileOpen(false);
                        setTimeout(() => setMobileOpen(false), 50);
                        setTimeout(() => setMobileOpen(false), 200);
                    }}
                    className={`mb-1 mx-3 rounded-xl cursor-pointer transition-all duration-300 ${selectedTab === item.text ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/25" : "text-gray-300 hover:bg-gray-700/50 hover:text-white"}`}
                    sx={{
                        "&:hover": {
                            transform: selectedTab === item.text ? "none" : "translateX(6px)",
                        },
                    }}
                >
                    <ListItemIcon className="min-w-0 mr-3">
                        <Box
                            className={`p-1 rounded-lg ${selectedTab === item.text ? "bg-white/20 text-white" : "bg-transparent"}`}
                        >
                            {React.cloneElement(item.icon, {
                                className: selectedTab === item.text ? "text-white" : item.text === "Logout" ? "text-red-600" : "text-blue-600",
                            })}
                        </Box>
                    </ListItemIcon>
                    <ListItemText
                        primary={item.text}
                        className={selectedTab === item.text ? "font-semibold" : ""}
                    />
                    {selectedTab === item.text && (<Box className="w-2 h-2 bg-white rounded-full animate-pulse"/>)}
                </ListItem>))}
            </List>
        </Box>
    </Box>);

    return (<Box className="flex bg-gray-50 min-h-screen">
        {/* Mobile Menu Button */}
        <Box className="md:hidden fixed top-4 right-4 z-[30]">
            <IconButton
                onClick={() => setMobileOpen(true)}
                className="bg-white shadow-lg hover:bg-gray-50"
                size="large"
            >
                <MenuIcon/>
            </IconButton>
        </Box>

        {/* Mobile Drawer */}
        <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{
                keepMounted: false,
                disableEscapeKeyDown: false,
                disableAutoFocus: true,
                disableEnforceFocus: true,
                disableRestoreFocus: true,
                onBackdropClick: () => setMobileOpen(false),
            }}
            sx={{
                display: {xs: "block", md: "none"}, "& .MuiDrawer-paper": {
                    width: 280, boxSizing: "border-box",
                }, zIndex: 55,
            }}
        >
            {drawerContent}
        </Drawer>

        {/* Desktop Drawer */}
        <Drawer
            variant="permanent"
            sx={{
                display: {xs: "none", md: "block"}, "& .MuiDrawer-paper": {
                    width: 280, boxSizing: "border-box",
                }, zIndex: 10,
            }}
            open
        >
            {drawerContent}
        </Drawer>

        {/* Main Content */}
        <Box className="flex-1 md:ml-[280px]">
            {/* Header */}
            <Box className="bg-white shadow-lg border-b border-gray-200 sticky top-0 z-20">
                <Box className="flex items-center justify-between p-4 md:p-6">
                    <Box className="flex items-center gap-3">
                        <Box
                            className={`p-3 rounded-xl shadow-lg ${selectedTab === "Dashboard" && "bg-gradient-to-r from-blue-500 to-indigo-600"} ${selectedTab === "Hostel History" && "bg-gradient-to-r from-blue-600 to-blue-700"} ${selectedTab === "Library History" && "bg-gradient-to-r from-green-600 to-green-700"} ${selectedTab === "Complaints" && "bg-gradient-to-r from-red-600 to-red-700"} ${selectedTab === "Bill" && "bg-gradient-to-r from-yellow-600 to-orange-600"} ${selectedTab === "Review" && "bg-gradient-to-r from-yellow-500 to-orange-600"}`}
                        >
                            {selectedTab === "Dashboard" && (<Dashboard className="text-white text-2xl"/>)}
                            {selectedTab === "Hostel History" && (
                                <ApartmentOutlinedIcon className="text-white text-2xl"/>)}
                            {selectedTab === "Library History" && (
                                <LocalLibraryOutlinedIcon className="text-white text-2xl"/>)}
                            {selectedTab === "Complaints" && (<ReportProblemIcon className="text-white text-2xl"/>)}
                            {selectedTab === "Bill" && (<ReceiptOutlinedIcon className="text-white text-2xl"/>)}
                            {selectedTab === "Review" && (<StarRate className="text-white text-2xl"/>)}
                        </Box>
                        <Box>
                <span className="mb-1 text-lg sm:text-3xl font-bold text-gray-800">
                  {selectedTab}
                </span>
                            <Typography sx={{fontSize: "16px"}} className="text-gray-600">
                                {selectedTab === "Dashboard" && "View bed and seat"}
                                {selectedTab === "Hostel History" && "Hostel booking records"}
                                {selectedTab === "Library History" && "Library booking records"}
                                {selectedTab === "Complaints" && "Track complaints"}
                                {selectedTab === "Bill" && "Generate monthly bills"}
                                {selectedTab === "Review" && "Reviews and feedback"}
                            </Typography>
                        </Box>
                    </Box>
                    <Box className="flex items-center gap-2 md:gap-3">
                        <Box
                            className="hidden md:flex items-center gap-2 text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded-lg">
                            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                            <span>Last updated:</span>
                            <span className="font-medium">
                  {new Date().toLocaleTimeString()}
                </span>
                        </Box>
                        {profile && (<Box className="relative right-16 md:right-0">
                            <StudentProfile
                                open={dropdownOpen}
                                onClose={() => setDropdownOpen(false)}
                                onProfileUpdate={() => {
                                    fetchAllData();
                                }}
                            />
                            <Avatar
                                src={profile.profile_photo || `https://ui-avatars.com/api/?name=${profile.first_name}+${profile.last_name}`}
                                alt="profile"
                                className="w-10 h-10 border-2 border-white shadow-md hover:scale-105 transition-transform cursor-pointer"
                                onClick={toggleDropdown}
                            />
                        </Box>)}
                    </Box>
                </Box>
            </Box>

            {/* Content Area */}
            <Box className="min-h-screen">
                {selectedTab === "Dashboard" && (
                    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 md:p-6">
                        <div className="max-w-full mx-auto space-y-4 md:space-y-6">
                            <StudentBedSeatNotes />
                            {/* Availability Maps Section */}
                            <div className="bg-white rounded-xl shadow-lg p-3 md:p-6 dashboard-card">
                                <div className="flex items-center justify-between mb-3 md:mb-6">
                                    <div className="flex items-center gap-2 md:gap-3">
                                        <div
                                            className="p-2 md:p-3 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl">
                                            <BusinessRoundedIcon className="text-white text-lg md:text-2xl"/>
                                        </div>
                                        <div>
                                            <h2 className="text-lg md:text-2xl font-bold text-gray-800">
                                                Availability Maps
                                            </h2>
                                            <p className="text-gray-600 text-xs md:text-sm">
                                                Bed and seat availability
                                            </p>
                                        </div>
                                    </div>
                                    <Button
                                        variant="outlined"
                                        onClick={refreshAvailabilityMaps}
                                        disabled={isRefreshing}
                                        sx={{
                                            borderRadius: "12px", borderColor: "#8b5cf6", color: "#8b5cf6", "&:hover": {
                                                borderColor: "#7c3aed", backgroundColor: "rgba(139, 92, 246, 0.04)",
                                            }, "&:disabled": {
                                                borderColor: "#d1d5db", color: "#9ca3af",
                                            }, minWidth: "40px", width: "40px", height: "40px",
                                        }}
                                    >
                                        <RefreshIcon
                                            className={`${isRefreshing ? "animate-spin" : ""}`}
                                            fontSize="small"
                                        />
                                    </Button>
                                </div>

                                <div className="bg-gray-50 rounded-xl p-2 md:p-4 mb-3 md:mb-4">
                                    <Tabs
                                        value={activeTab}
                                        onChange={handleTabChange}
                                        indicatorColor="primary"
                                        textColor="primary"
                                        className="mb-3 md:mb-4"
                                        sx={{
                                            "& .MuiTab-root": {
                                                fontWeight: 600,
                                                fontSize: "0.875rem",
                                                borderRadius: "12px",
                                                marginRight: "8px",
                                            }, "& .Mui-selected": {
                                                backgroundColor: "rgba(59, 130, 246, 0.1)",
                                            },
                                        }}
                                    >
                                        <Tab
                                            label={<div className="flex items-center gap-1 md:gap-2">
                                                <BusinessRoundedIcon fontSize="small"/>
                                                <span className="hidden sm:inline">
                              Hostel Bed Availability
                            </span>
                                                <span className="sm:hidden">Hostel</span>
                                            </div>}
                                        />
                                        <Tab
                                            label={<div className="flex items-center gap-1 md:gap-2">
                                                <LocalLibraryOutlinedIcon fontSize="small"/>
                                                <span className="hidden sm:inline">
                              Study Centre Seat Availability
                            </span>
                                                <span className="sm:hidden">Library</span>
                                            </div>}
                                        />
                                    </Tabs>

                                    <div className="mt-3 md:mt-4">
                                        {activeTab === 0 && (<div className="bg-white rounded-lg p-2 md:p-4 shadow-sm">
                                            <SeatMap
                                                data={hostelSeats}
                                                student={profile}
                                                onSeatClick={handleHostelSeatClick}
                                            />
                                        </div>)}
                                        {activeTab === 1 && (<div className="bg-white rounded-lg p-2 md:p-4 shadow-sm">
                                            <SeatMap
                                                data={librarySeats}
                                                student={profile}
                                                onSeatClick={handleLibrarySeatClick}
                                            />
                                        </div>)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>)}

                {selectedTab === "Hostel History" && <HostelBookingHistory/>}
                {selectedTab === "Library History" && <LibraryBookingHistory/>}
                {selectedTab === "Complaints" && <StudentComplaints/>}
                {selectedTab === "Bill" && <StudentBill/>}
                {selectedTab === "Review" && <StudentReview/>}
            </Box>

            {/* Booking Forms */}
            <HostelBookingForm
                bedId={selectedBedId}
                open={!!selectedBedId}
                onClose={() => {
                    setSelectedBedId(null);
                    localStorage.setItem("student_selected_menu", "Dashboard");
                    localStorage.setItem("student_dashboard_tab", 0);
                    setSelectedTab("Dashboard");
                    setActiveTab(0);
                }}
                onSuccess={() => {
                    localStorage.setItem("student_selected_menu", "Dashboard");
                    localStorage.setItem("student_dashboard_tab", 0);
                    setSelectedTab("Dashboard");
                    setActiveTab(0);
                    fetchAllData();
                }}
            />
            <LibraryBookingForm
                seatId={selectedSeatId}
                open={!!selectedSeatId}
                onClose={() => {
                    setSelectedSeatId(null);
                    localStorage.setItem("student_selected_menu", "Dashboard");
                    localStorage.setItem("student_dashboard_tab", 1);
                    setSelectedTab("Dashboard");
                    setActiveTab(1);
                }}
                onSuccess={() => {
                    localStorage.setItem("student_selected_menu", "Dashboard");
                    localStorage.setItem("student_dashboard_tab", 1);
                    setSelectedTab("Dashboard");
                    setActiveTab(1);
                    fetchAllData();
                }}
            />
        </Box>
    </Box>);
};

export default StudentDashboard;
